import React, { useState, useRef } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader, EmptyState } from '@/components/Shared';
import { ScanLine, Upload, Loader2, Trash2, CheckCircle2, FileText, Merge } from 'lucide-react';
import { toast } from 'sonner';

export default function OCRImport() {
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [rows, setRows] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [mode, setMode] = useState('add');
  const [applying, setApplying] = useState(false);
  const inputRef = useRef();

  const extract = async () => {
    if (!file) { toast.error('Choose an image or PDF first'); return; }
    setExtracting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/ocr/extract', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRows(data.rows.map((r, i) => ({ ...r, _id: i })));
      setWarnings(data.warnings || []);
      toast.success(`Extracted ${data.rows.length} rows`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Extraction failed'); }
    finally { setExtracting(false); }
  };

  const updateRow = (id, key, val) => setRows(rows.map((r) => r._id === id ? { ...r, [key]: key === 'quantity' ? Number(val) : val } : r));
  const removeRow = (id) => setRows(rows.filter((r) => r._id !== id));

  const mergeDuplicates = () => {
    const map = {};
    rows.forEach((r) => {
      const key = (r.sku || r.product_name).toLowerCase();
      if (map[key]) map[key].quantity += r.quantity;
      else map[key] = { ...r };
    });
    setRows(Object.values(map).map((r, i) => ({ ...r, _id: i })));
    toast.success('Duplicates merged');
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post('/ocr/apply', { rows: rows.map(({ _id, existing, existing_quantity, ...r }) => r), mode });
      if (mode === 'draft') toast.success('Saved as draft');
      else toast.success(`Applied: ${data.created} created, ${data.updated} updated`);
      setRows(null); setFile(null); if (inputRef.current) inputRef.current.value = '';
    } catch (e) { toast.error(e?.response?.data?.detail || 'Apply failed'); }
    finally { setApplying(false); }
  };

  return (
    <div>
      <PageHeader title="OCR Inventory Import" subtitle="Upload a photo, screenshot or PDF — AI extracts SKUs, names & quantities" testid="ocr-header" />

      {!rows && (
        <Card className="p-8">
          <div className="flex flex-col items-center text-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-teal-50 text-teal-700"><ScanLine className="h-7 w-7" /></div>
            <div>
              <h3 className="font-heading font-semibold">Upload inventory sheet</h3>
              <p className="text-sm text-muted-foreground">Supports JPG, PNG, and PDF inventory reports.</p>
            </div>
            <Input ref={inputRef} type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files[0])} className="max-w-sm" data-testid="ocr-upload-input" />
            {file && <p className="text-sm flex items-center gap-2"><FileText className="h-4 w-4" /> {file.name}</p>}
            <Button onClick={extract} disabled={extracting || !file} data-testid="ocr-extract-button">
              {extracting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Extracting…</> : <><Upload className="h-4 w-4 mr-2" /> Extract Inventory</>}
            </Button>
          </div>
        </Card>
      )}

      {rows && (
        <Card className="p-0 overflow-hidden">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b p-4">
            <div>
              <h3 className="font-heading font-semibold">Review {rows.length} rows</h3>
              {warnings.length > 0 && <p className="text-xs text-amber-700 mt-1">{warnings.join(' · ')}</p>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={mergeDuplicates} data-testid="ocr-merge-button"><Merge className="h-4 w-4 mr-1" /> Merge Duplicates</Button>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="w-[160px]" data-testid="ocr-review-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add to inventory</SelectItem>
                  <SelectItem value="replace">Replace quantities</SelectItem>
                  <SelectItem value="draft">Save as draft</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={apply} disabled={applying} data-testid="ocr-review-apply-button">
                {applying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Apply
              </Button>
            </div>
          </div>
          {rows.length === 0 ? <div className="p-6"><EmptyState icon={ScanLine} title="No rows" description="All rows removed." /></div> : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Product Name</TableHead><TableHead>Brand</TableHead><TableHead className="w-28">Qty</TableHead><TableHead>State</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r._id} data-testid="ocr-review-row">
                      <TableCell><Input value={r.sku} onChange={(e) => updateRow(r._id, 'sku', e.target.value)} className="h-8 font-mono text-xs" /></TableCell>
                      <TableCell><Input value={r.product_name} onChange={(e) => updateRow(r._id, 'product_name', e.target.value)} className="h-8" /></TableCell>
                      <TableCell><Input value={r.brand} onChange={(e) => updateRow(r._id, 'brand', e.target.value)} className="h-8" /></TableCell>
                      <TableCell><Input type="number" value={r.quantity} onChange={(e) => updateRow(r._id, 'quantity', e.target.value)} className="h-8 tabular-nums" /></TableCell>
                      <TableCell>{r.existing ? <span className="text-xs text-blue-700">Exists ({r.existing_quantity})</span> : <span className="text-xs text-emerald-700">New</span>}</TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => removeRow(r._id)} data-testid="ocr-review-row-remove-button"><Trash2 className="h-4 w-4 text-red-600" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
